import { ITEM_TYPES } from 'constants/itemType';
import { NotificationPriority } from 'hooks/useNotification';
import type { UserNotificationWithDetails } from 'hooks/useUserNotifications';
import {
  formatStoreListLabel,
  HELENE_ADVISORIES,
  storesInCone,
  type CfaDemoStore,
} from 'components/MapPage/CorporateViewTabs/LayersTab/cfa-demo/historicalHurricanesHeleneData';
import {
  getTennesseeMemphisRegionalThreats,
  tennesseeMemphisCreateIncidentPrefill,
  TNNG_MEMPHIS_APP_MESSAGE,
  TNNG_MEMPHIS_THREAT_PROVENANCE,
} from './tennesseeMemphisThreatNotification';

export type MemphisThreatVariant = 'cfa' | 'tnng';

/** Stable id for the hardcoded Memphis threat inbox item. */
export const MEMPHIS_THREAT_NOTIFICATION_ID = 'demo-memphis-flood-power-outage';

/** Fixed timestamp so the list order/display stays stable across refetches. */
const MEMPHIS_THREAT_SENT_AT = '2025-03-30T15:30:00.000Z';

const TS_ADVISORY =
  HELENE_ADVISORIES.find((a) => a.category === 'TS') ?? HELENE_ADVISORIES[1];

export const MEMPHIS_THREAT_PROVENANCE = 'NHC advisory (synthetic Helene demo)';

export const MEMPHIS_THRESHOLD_DESCRIPTION =
  'Tropical storm category threshold exceeded — Helene forecast cone contains Chick-fil-A resources';

export const MEMPHIS_LINKED_EVENT_NAME =
  'Tropical Storm Helene — CFA stores in forecast cone';

export type MemphisAssetOperationalStatus =
  | 'Operational'
  | 'Partially Operational'
  | 'Not Operational';

export type MemphisAssetAtRisk = {
  id: string;
  resource: string;
  risk: string;
  location: [number, number];
  operationalStatus: MemphisAssetOperationalStatus;
};

export type MemphisRegionalThreats = {
  region: string;
  description: string;
  threats: MemphisAssetAtRisk[];
  responseChecklist: { id: string; label: string }[];
};

/**
 * Stable checklist labels — also used as org-action names when CFA ensures DB
 * rows.
 */
export const MEMPHIS_RESPONSE_CHECKLIST_ITEMS = [
  {
    id: 'memphis-checklist-notify-ops',
    label:
      'Notify franchise operators (SMS/email) for all Chick-fil-A stores inside the Helene forecast cone.',
  },
  {
    id: 'memphis-checklist-assign-prep',
    label:
      'Assign Helene prep tasks in Response Checklist / Org Actions for each store in the cone.',
  },
  {
    id: 'memphis-checklist-monitor-category',
    label:
      'Monitor NHC advisories for tropical storm → hurricane category upgrades and re-run cone impact.',
  },
  {
    id: 'memphis-checklist-stand-up-workspace',
    label:
      'Stand up an incident workspace for Tropical Storm Helene and track CFA Southeast readiness.',
  },
] as const;

export const MEMPHIS_RESPONSE_CHECKLIST_LABELS =
  MEMPHIS_RESPONSE_CHECKLIST_ITEMS.map((item) => item.label);

export const MEMPHIS_ORG_ACTION_DESCRIPTION =
  'CFA Helene demo — auto-created from Memphis threat Response Checklist. Planned / Unassigned.';

/**
 * Exact labels of the four Helene / Memphis demo Response Checklist actions.
 * Used when the threshold event was created without materialized checklist
 * ids.
 */
export function isMemphisThreatOrgActionName(
  name: string | null | undefined
): boolean {
  return (
    !!name &&
    (MEMPHIS_RESPONSE_CHECKLIST_LABELS as readonly string[]).includes(name)
  );
}

/** Notes marker for Helene cone resource sets (idempotent ensure). */
export const HELENE_CONE_RESOURCE_NOTES_PREFIX = 'cfa-helene-store:';

export function heleneConeResourceNotesMarker(storeId: string): string {
  return `${HELENE_CONE_RESOURCE_NOTES_PREFIX}${storeId}`;
}

export function heleneStoreIdFromResourceNotes(
  notes: string | null | undefined
): string | undefined {
  if (!notes?.startsWith(HELENE_CONE_RESOURCE_NOTES_PREFIX)) return undefined;
  const id = notes.slice(HELENE_CONE_RESOURCE_NOTES_PREFIX.length);
  return id || undefined;
}

export function isHeleneConeResourceSet(resource: {
  notes?: string | null;
}): boolean {
  return !!resource.notes?.startsWith(HELENE_CONE_RESOURCE_NOTES_PREFIX);
}

function buildAssetFromStore(store: CfaDemoStore): MemphisAssetAtRisk {
  return {
    id: store.id,
    resource: formatStoreListLabel(store),
    risk:
      `${store.name} (${store.city}, ${store.state}) sits inside the Helene ` +
      `forecast cone while the system is at Tropical Storm intensity ` +
      `(${TS_ADVISORY.maxWindMph} mph). Secure site, confirm cold-chain, and report status to Regional Ops.`,
    location: [store.lon, store.lat],
    operationalStatus: 'Partially Operational',
  };
}

export function getMemphisRegionalThreats(
  variant: MemphisThreatVariant = 'cfa'
): MemphisRegionalThreats {
  if (variant === 'tnng') {
    return getTennesseeMemphisRegionalThreats();
  }
  const inCone = storesInCone();
  return {
    region: 'Chick-fil-A Southeast — Helene forecast cone',
    description:
      'Tropical Storm Helene places the following Chick-fil-A resources inside the National Hurricane Center forecast cone at elevated risk:',
    threats: inCone.map(buildAssetFromStore),
    responseChecklist: MEMPHIS_RESPONSE_CHECKLIST_ITEMS.map((item) => ({
      id: item.id,
      label: item.label,
    })),
  };
}

/** Threshold callout rows shown above the linked event (hub-style data source). */
export const MEMPHIS_THREAT_THRESHOLDS = [
  {
    input: 'Storm category',
    region: 'NHC advisory (Helene)',
    threshold: '≥ Tropical Storm (TS)',
    observed: `TS — ${TS_ADVISORY.label} (${TS_ADVISORY.maxWindMph} mph)`,
  },
  {
    input: 'Forecast cone vs CFA resources',
    region: 'Chick-fil-A Southeast',
    threshold: '≥ 1 store in cone',
    observed: `${storesInCone().length} stores in cone`,
  },
] as const;

export const MEMPHIS_THREAT_RELATED_EVENTS = [
  MEMPHIS_LINKED_EVENT_NAME,
  MEMPHIS_THRESHOLD_DESCRIPTION,
  ...MEMPHIS_THREAT_THRESHOLDS.map(
    (row) =>
      `${row.input} in ${row.region}: ${row.observed} (threshold ${row.threshold})`
  ),
];

export const MEMPHIS_DEMO_INCIDENT_TYPE = 'Tropical Storm';

export const MEMPHIS_DEMO_AOR = {
  value: 'demo-cfa-southeast-helene-aor',
  label: 'CFA Southeast — Helene cone',
} as const;

/**
 * Router state for the create-incident wizard when it is launched from the
 * Memphis threat demo, so the incident lands pre-scoped to that scenario.
 */
export function memphisCreateIncidentPrefill(
  heleneEventId?: string,
  variant: MemphisThreatVariant = 'cfa'
) {
  if (variant === 'tnng') {
    return tennesseeMemphisCreateIncidentPrefill(heleneEventId);
  }
  return {
    initialRelatedEvents: heleneEventId
      ? [heleneEventId]
      : [...MEMPHIS_THREAT_RELATED_EVENTS],
    initialIncidentTypes: [MEMPHIS_DEMO_INCIDENT_TYPE],
    initialAorSelections: [MEMPHIS_DEMO_AOR],
  };
}

export function isHardcodedMemphisThreatNotification(
  notificationId: string | null | undefined
): boolean {
  return notificationId === MEMPHIS_THREAT_NOTIFICATION_ID;
}

export function getMemphisThreatNotification(
  groupId: string,
  variant: MemphisThreatVariant = 'cfa'
): UserNotificationWithDetails {
  return {
    userGuid: 'system',
    notificationId: MEMPHIS_THREAT_NOTIFICATION_ID,
    groupId,
    archived: false,
    acknowledged: false,
    canArchive: false,
    createdAt: MEMPHIS_THREAT_SENT_AT,
    acknowledgedAt: null,
    archivedAt: null,
    priority: NotificationPriority.WARNING,
    status: 'sent',
    acknowledgementRequired: false,
    sourceItemId: '',
    sourceItemType: ITEM_TYPES.Sitrep,
    sentAt: MEMPHIS_THREAT_SENT_AT,
    scheduledDate: null,
    incidentId: null,
    aors: [],
    appMessage:
      variant === 'tnng'
        ? TNNG_MEMPHIS_APP_MESSAGE
        : 'Likely impact to CFA resources due to Tropical Storm in Gulf of Mexico.',
    emailSubject: null,
    emailMessage: null,
    smsMessage: null,
    teamsMessage: null,
    sendEmail: null,
    sendSms: null,
    sendTeams: null,
    actionType: null,
    createdByName:
      variant === 'tnng'
        ? TNNG_MEMPHIS_THREAT_PROVENANCE
        : MEMPHIS_THREAT_PROVENANCE,
  };
}
